import React from 'react';
import { ChevronLeft, ChevronRight, Lightbulb } from 'lucide-react';
import DropZone from './DropZone';
import CreativeCard from './CreativeCard';
import { getContentForDate, hasScheduledContent } from '../../utils/schedulerAI';

/**
 * CalendarGrid Component
 * Calendar grid with thumbnail cells and drop zones for scheduling
 *
 * @param {Object} props
 * @param {Date} props.currentDate - The current month being displayed
 * @param {function} props.onPrevMonth - Callback to go to previous month
 * @param {function} props.onNextMonth - Callback to go to next month
 * @param {Array} props.scheduledPosts - Array of scheduled posts
 * @param {Array} props.creatives - Array of all creatives
 * @param {function} props.onDrop - Callback when a creative is dropped on a date
 * @param {function} props.onCreativeClick - Callback when a scheduled creative is clicked
 * @param {function} props.onGapClick - Callback when a gap day is clicked
 * @param {function} props.onDayClick - Callback when any day is clicked
 */
const CalendarGrid = ({
    currentDate,
    onPrevMonth,
    onNextMonth,
    scheduledPosts = [],
    creatives = [],
    onDrop,
    onCreativeClick,
    onGapClick,
    onDayClick
}) => {
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getPostsForDate = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        return getContentForDate(date, scheduledPosts, creatives);
    };

    const isToday = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        date.setHours(0, 0, 0, 0);
        return date.getTime() === today.getTime();
    };

    const isPast = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        date.setHours(0, 0, 0, 0);
        return date < today;
    };

    const isGap = (day) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        date.setHours(0, 0, 0, 0);
        // Only show gap indicator for future dates
        if (date < today) return false;
        return !hasScheduledContent(date, scheduledPosts);
    };

    const handleDrop = (day) => (dragData) => {
        const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        if (onDrop) {
            onDrop(dragData, targetDate);
        }
    };

    const handleGapClick = (day) => (e) => {
        e.stopPropagation();
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        if (onGapClick) {
            onGapClick(date);
        }
    };

    const handleDayClick = (day) => () => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        if (onDayClick) {
            onDayClick(date);
        }
    };

    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-4)'
            }}>
                <h2 style={{ margin: 0 }}>
                    {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <button className="btn btn-icon btn-secondary" onClick={onPrevMonth}>
                        <ChevronLeft size={20} />
                    </button>
                    <button className="btn btn-icon btn-secondary" onClick={onNextMonth}>
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>

            {/* Day headers */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 'var(--space-1)',
                marginBottom: 'var(--space-2)'
            }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} style={{
                        textAlign: 'center',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: 'var(--color-text-secondary)',
                        padding: 'var(--space-2)'
                    }}>
                        {day}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 'var(--space-1)'
            }}>
                {/* Empty cells for days before the month starts */}
                {[...Array(firstDayOfMonth)].map((_, i) => (
                    <div key={`empty-${i}`} style={{
                        minHeight: '120px',
                        background: 'var(--color-bg-tertiary)',
                        borderRadius: 'var(--radius-md)',
                        opacity: 0.3
                    }} />
                ))}

                {/* Day cells */}
                {[...Array(daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const postsForDay = getPostsForDate(day);
                    const todayClass = isToday(day);
                    const pastDay = isPast(day);
                    const gapDay = isGap(day);

                    return (
                        <DropZone
                            key={day}
                            onDrop={handleDrop(day)}
                            disabled={pastDay}
                            style={{
                                minHeight: '120px',
                                padding: 'var(--space-2)',
                                background: todayClass ? 'var(--color-primary-light)' : 'var(--color-surface)',
                                borderRadius: 'var(--radius-md)',
                                border: todayClass
                                    ? '2px solid var(--color-primary)'
                                    : gapDay
                                        ? '2px dashed var(--color-warning)'
                                        : '1px solid var(--color-border-light)',
                                cursor: 'pointer',
                                opacity: pastDay ? 0.5 : 1,
                                position: 'relative'
                            }}
                        >
                            <div onClick={handleDayClick(day)}>
                                {/* Day number */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 'var(--space-2)'
                                }}>
                                    <span style={{
                                        fontWeight: 600,
                                        fontSize: 'var(--font-size-sm)',
                                        color: todayClass ? 'var(--color-primary)' : 'var(--color-text-primary)'
                                    }}>
                                        {day}
                                    </span>

                                    {/* Gap indicator */}
                                    {gapDay && !pastDay && (
                                        <button
                                            onClick={handleGapClick(day)}
                                            style={{
                                                background: 'var(--color-warning)',
                                                border: 'none',
                                                borderRadius: 'var(--radius-full)',
                                                width: '20px',
                                                height: '20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                padding: 0
                                            }}
                                            title="Click for suggestions"
                                        >
                                            <Lightbulb size={12} color="white" />
                                        </button>
                                    )}
                                </div>

                                {/* Scheduled content thumbnails */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: postsForDay.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                                    gap: 'var(--space-1)'
                                }}>
                                    {postsForDay.slice(0, 4).map((post, idx) => (
                                        <div
                                            key={post.id || idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onCreativeClick && post.creative) {
                                                    onCreativeClick(post.creative, post);
                                                }
                                            }}
                                        >
                                            {post.creative ? (
                                                <CreativeCard
                                                    creative={post.creative}
                                                    compact={true}
                                                    draggable={!pastDay}
                                                    status={post.status}
                                                    publishedAt={post.publishedAt}
                                                    lastError={post.lastError}
                                                    publishAttempts={post.publishAttempts}
                                                />
                                            ) : (
                                                <div style={{
                                                    width: '100%',
                                                    aspectRatio: '1/1',
                                                    background: 'var(--color-bg-tertiary)',
                                                    borderRadius: 'var(--radius-sm)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    color: 'var(--color-text-tertiary)',
                                                    position: 'relative'
                                                }}>
                                                    {post.creativeTitle?.charAt(0) || '?'}
                                                    {/* Status indicator for posts without creative data */}
                                                    {post.status === 'published' && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: '2px',
                                                            left: '2px',
                                                            background: 'var(--success-500)',
                                                            borderRadius: '50%',
                                                            width: '12px',
                                                            height: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}>
                                                            <span style={{ color: 'white', fontSize: '8px' }}>✓</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* More indicator */}
                                {postsForDay.length > 4 && (
                                    <div style={{
                                        fontSize: '10px',
                                        color: 'var(--color-text-secondary)',
                                        textAlign: 'center',
                                        marginTop: 'var(--space-1)'
                                    }}>
                                        +{postsForDay.length - 4} more
                                    </div>
                                )}
                            </div>
                        </DropZone>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarGrid;
